/**
 * Safe round-trip between env.Config's two sibling helm-override maps and a flat
 * editable entry list. The write path MUST preserve every sibling key in
 * env.Config — EnvironmentService.Update on the backend replaces env.Config
 * wholesale, so any dropped field is lost forever. Unit tests enforce this.
 */

export interface HelmOverrideEntry {
  key: string
  value: string
  type: 'auto' | 'string'
}

/**
 * Result of reading env.Config. `entries` is the UI-editable list. `dropped`
 * contains any raw entries that were skipped because their value was not a
 * string — these correspond exactly to the entries the backend `toStringMap`
 * helper drops with a warning. Surfacing them lets the card show a banner so
 * the user sees the same reality the deploy pipeline sees (review finding I-A).
 */
export interface ReadResult {
  entries: HelmOverrideEntry[]
  dropped: DroppedEntry[]
}

export interface DroppedEntry {
  source: 'helm_overrides' | 'helm_overrides_string'
  key: string
  actualType: string
}

function describeType(v: unknown): string {
  if (v === null) return 'null'
  if (Array.isArray(v)) return 'array'
  return typeof v
}

function readStringMap(
  raw: unknown,
  source: 'helm_overrides' | 'helm_overrides_string',
  dropped: DroppedEntry[]
): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') {
      out[k] = v
    } else {
      // Mirrors backend toStringMap (infra_operation_service.go) which skips
      // non-string values and logs a warning. Coercion via String(v) would
      // launder JSONB garbage (null → "null", {} → "[object Object]") into
      // valid-looking overrides on the next save — see PR review finding I-A.
      dropped.push({ source, key: k, actualType: describeType(v) })
    }
  }
  return out
}

/**
 * Reconstructs a unified, key-sorted list from env.Config's helm_overrides and
 * helm_overrides_string maps. Tolerates missing maps and both camelCase and
 * snake_case field spellings. Non-string values are dropped and reported so
 * the UI can warn.
 */
export function readHelmOverrides(
  config: Record<string, unknown>
): ReadResult {
  const dropped: DroppedEntry[] = []
  const autoMap = readStringMap(
    config['helm_overrides'] ?? config['helmOverrides'],
    'helm_overrides',
    dropped
  )
  const stringMap = readStringMap(
    config['helm_overrides_string'] ?? config['helmOverridesString'],
    'helm_overrides_string',
    dropped
  )

  const entries: HelmOverrideEntry[] = [
    ...Object.entries(autoMap).map<HelmOverrideEntry>(([key, value]) => ({ key, value, type: 'auto' })),
    ...Object.entries(stringMap).map<HelmOverrideEntry>(([key, value]) => ({ key, value, type: 'string' })),
  ]

  entries.sort((a, b) => a.key.localeCompare(b.key))
  return { entries, dropped }
}

/**
 * Produces a new env.Config with helm_overrides and helm_overrides_string rebuilt
 * from the entry list. All other keys in the input config are preserved unchanged.
 * Empty maps are omitted from the output (not serialised as `{}`) — the backend
 * reader treats missing keys and empty keys identically, and omitting keeps the
 * JSONB clean. Keys are trimmed before emission so "  foo  " and "foo" collapse
 * to the same helm path (review finding I-C). The input config is NOT mutated.
 */
export function writeHelmOverrides(
  currentConfig: Record<string, unknown>,
  entries: HelmOverrideEntry[]
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...currentConfig }
  delete next['helm_overrides']
  delete next['helmOverrides']
  delete next['helm_overrides_string']
  delete next['helmOverridesString']

  const autoMap: Record<string, string> = {}
  const stringMap: Record<string, string> = {}
  for (const entry of entries) {
    const trimmedKey = entry.key.trim()
    if (trimmedKey === '') continue
    if (entry.type === 'auto') {
      autoMap[trimmedKey] = entry.value
    } else {
      stringMap[trimmedKey] = entry.value
    }
  }

  if (Object.keys(autoMap).length > 0) {
    next['helm_overrides'] = autoMap
  }
  if (Object.keys(stringMap).length > 0) {
    next['helm_overrides_string'] = stringMap
  }

  return next
}

/**
 * Returns the first duplicate helm path in the draft list (across BOTH auto
 * and string buckets), or null if none. Uses trimmed keys so "foo" vs "foo "
 * count as the same path. Empty/whitespace-only keys are ignored.
 */
export function findDuplicateKey(entries: HelmOverrideEntry[]): string | null {
  const seen = new Set<string>()
  for (const e of entries) {
    const k = e.key.trim()
    if (k === '') continue
    if (seen.has(k)) return k
    seen.add(k)
  }
  return null
}
